import { Component } from 'react';

/**
 * Global error boundary — catches any uncaught React rendering error
 * and shows a friendly "Something went wrong" screen instead of a
 * blank white/black page.
 *
 * Place this at the root of the component tree (in main.jsx) so it
 * catches errors from AuthProvider, App, and everything below.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100dvh',
          width: '100%',
          background: '#1a1a1a',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        }}>
          {/* Warning icon */}
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(211, 71, 74, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 24,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h1 style={{
            color: '#ffffff',
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: 8,
            textAlign: 'center',
          }}>
            Something went wrong
          </h1>

          <p style={{
            color: '#999',
            fontSize: '0.875rem',
            textAlign: 'center',
            maxWidth: 320,
            lineHeight: 1.5,
            marginBottom: 24,
          }}>
            The app ran into an unexpected error. Tap below to reload — your data is safe.
          </p>

          <button
            onClick={this.handleReload}
            style={{
              background: '#D3474A',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '14px 32px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              marginBottom: 16,
            }}
          >
            Reload App
          </button>

          {/* Error details (collapsed by default) */}
          {this.state.error && (
            <details style={{ maxWidth: 320, width: '100%' }}>
              <summary style={{
                color: '#666',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textAlign: 'center',
              }}>
                Error details
              </summary>
              <pre style={{
                color: '#888',
                fontSize: '0.6875rem',
                background: '#222',
                borderRadius: 8,
                padding: 12,
                marginTop: 8,
                overflow: 'auto',
                maxHeight: 200,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {this.state.error.toString()}
              </pre>
            </details>
          )}

          <p style={{
            color: '#555',
            fontSize: '0.75rem',
            marginTop: 32,
          }}>
            Proper Miles Mobile Garage
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
